import clsx from "clsx";
import { Upload } from "lucide-react";
import { FC, useState } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import { FormattedMessage, useIntl } from "react-intl";
import { ConditionalTooltip } from "~/components/ConditionalTooltip";
import { Button } from "~/components/ui/button";
import { ACCEPTED_FILE_TYPES } from "~/constants/index";
import { trackEvent } from "~/utils/analytics";

type Props = {
  onDrop: (files: File[]) => void;
  className?: string;
  disabled?: boolean;
  isKnowledge?: boolean;
};

export const DocumentsDropzone: FC<Props> = ({
  onDrop,
  className,
  disabled,
  isKnowledge = false,
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [invalidFiles, setInvalidFiles] = useState<FileRejection[]>([]);
  const { formatMessage } = useIntl();

  const onDragEnter = () => {
    setIsDragActive(true);
  };

  const onDragLeave = () => {
    setIsDragActive(false);
  };

  const handleDrop = (
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ) => {
    setInvalidFiles(fileRejections);

    if (acceptedFiles.length > 0) {
      const totalSizeBytes = acceptedFiles.reduce(
        (sum, file) => sum + file.size,
        0
      );
      const fileTypes = [...new Set(acceptedFiles.map((file) => file.type))];

      trackEvent({
        eventName: "app/case/dropzone.area/files_selected",
        properties: {
          fileCount: acceptedFiles.length,
          totalSizeBytes,
          fileTypes,
        },
      });
    }

    onDrop(acceptedFiles);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDragEnter,
    onDragLeave,
    onDrop: (acceptedFiles, fileRejections) => {
      setIsDragActive(false);
      handleDrop(acceptedFiles, fileRejections);
    },
    accept: ACCEPTED_FILE_TYPES,
    validator: (file) => {
      if (
        ![
          "application/pdf",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "image/png",
          "image/jpeg",
        ].includes(file.type)
      ) {
        return {
          code: "file-invalid-type",
          message: formatMessage({ id: "documentsList.dropzone.invalidFiles" }),
        };
      }
      return null;
    },
    disabled: disabled,
  });

  return (
    <div
      {...getRootProps()}
      className={clsx(
        "flex items-center justify-center cursor-pointer",
        className
      )}
    >
      <div
        className={clsx(
          "border-dashed w-full flex-1 min-h-full rounded-xl border flex-col justify-center items-center gap-4 flex transition-all duration-300",
          {
            "border-blue-500": isDragActive,
            "border-zinc-200": !isDragActive,
            "cursor-not-allowed": disabled,
          }
        )}
      >
        <div
          className={clsx("flex flex-col items-center gap-4 w-full", {
            "opacity-50": isDragActive || disabled,
          })}
        >
          <div className="bg-white h-14 p-4 rounded-full border border-zinc-200 justify-start items-center gap-2.5 border-dashed flex">
            <Upload className="w-6 h-6 text-zinc-500" />
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-center text-zinc-500 text-sm font-semibold">
              {isKnowledge ? (
                <FormattedMessage id="documentsList.dropzone.dragAndDropTitleKnowledge" />
              ) : (
                <FormattedMessage id="documentsList.dropzone.dragAndDropTitle" />
              )}
            </p>
            <div className="flex justify-center pt-2">
              <ConditionalTooltip condition={disabled}>
                <Button
                  variant="secondary"
                  className="bg-blue-100 text-blue-600"
                  onClick={() => {
                    trackEvent({ eventName: "app/case/choose.button/click" });
                  }}
                  disabled={disabled}
                >
                  <FormattedMessage id="documentsList.dropzone.btn" />
                </Button>
              </ConditionalTooltip>
            </div>
            {invalidFiles.length > 0 && (
              <p className="text-sm text-red-500 text-center">
                <FormattedMessage id="documentsList.dropzone.invalidFiles" />
              </p>
            )}
          </div>
        </div>
      </div>
      <input
        {...getInputProps()}
        id="dropzone-file"
        type="file"
        className="hidden"
      />
    </div>
  );
};
